// Console Super Admin — Import massivo CSV (aziende e utenti/consulenti).
// Flusso: file → anteprima dry-run con errori per riga → esecuzione import.
import { useState } from "react";
import { Upload, FileSpreadsheet, Play, Eye } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const MODELS = {
  companies: {
    title: "Import Aziende",
    description: "Colonne: denominazione, partita_iva, codice_fiscale, nazione, citta, codice_ateco, settore, numero_dipendenti, admin_email, admin_full_name, reporting_year",
    sample: "denominazione;partita_iva;citta;admin_email;admin_full_name;reporting_year\nAzienda Esempio Srl;01234567890;Milano;admin@esempio.it;Mario Rossi;2025",
    endpoint: "/admin/platform/import/companies",
  },
  users: {
    title: "Import Utenti e Consulenti",
    description: "Colonne: email, full_name, tipo (CONSULENTE|AZIENDA), organization_name (consulenti), company_piva (azienda), role",
    sample: "email;full_name;tipo;organization_name;company_piva;role\ncons@studio.it;Laura Bianchi;CONSULENTE;Studio Demo;;LEAD_CONSULTANT\nutente@azienda.it;Paolo Verdi;AZIENDA;;01234567890;ADMIN_AZIENDA",
    endpoint: "/admin/platform/import/users",
  },
};

function ImportCard({ model }) {
  const cfg = MODELS[model];
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const call = async (dryRun) => {
    if (!file) {
      toast.error("Seleziona un file CSV");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`${cfg.endpoint}?dry_run=${dryRun}`, fd);
      if (dryRun) {
        setPreview(data);
        setResult(null);
      } else {
        setResult(data);
        setPreview(null);
        toast.success(`Import completato: ${data.ok}/${data.total} righe OK`);
      }
    } catch (e) {
      toast.error("Errore import", { description: formatApiError(e) });
    } finally {
      setBusy(false);
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([cfg.sample], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `modello_import_${model}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const report = preview || result;

  return (
    <Card className="border-slate-200" data-testid={`import-${model}-card`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <FileSpreadsheet className="w-4 h-4 text-green-700" aria-hidden="true" />
          {cfg.title}
        </CardTitle>
        <p className="text-xs text-slate-500">{cfg.description}</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <input type="file" accept=".csv" data-testid={`import-${model}-file`}
            className="text-xs text-slate-600 file:mr-2 file:rounded-md file:border file:border-slate-200 file:bg-white file:px-3 file:py-1.5 file:text-xs hover:file:bg-slate-50"
            onChange={(e) => { setFile(e.target.files[0] || null); setPreview(null); setResult(null); }} />
          <Button variant="outline" size="sm" onClick={downloadTemplate}
            data-testid={`import-${model}-template`}>Modello CSV</Button>
          <Button variant="outline" size="sm" disabled={!file || busy}
            onClick={() => call(true)} data-testid={`import-${model}-preview`}>
            <Eye className="w-3 h-3 mr-1" aria-hidden="true" /> Anteprima
          </Button>
          <Button size="sm" className="bg-green-700 hover:bg-green-800"
            disabled={!preview || preview.errors > 0 || busy}
            onClick={() => call(false)} data-testid={`import-${model}-execute`}>
            <Play className="w-3 h-3 mr-1" aria-hidden="true" /> Esegui import
          </Button>
        </div>
        {preview && preview.errors > 0 && (
          <p className="text-xs text-red-700" data-testid={`import-${model}-blocked`}>
            Correggi gli errori prima di eseguire: il pulsante è bloccato finché ci sono righe in errore.
          </p>
        )}
        {report && (
          <div className="space-y-2">
            <p className="text-xs text-slate-600" data-testid={`import-${model}-summary`}>
              {report.dry_run ? "Anteprima" : "Risultato"}: {report.ok}/{report.total} righe OK,{" "}
              {report.errors} errori
            </p>
            <div className="max-h-64 overflow-y-auto rounded-md border border-slate-200">
              <table className="w-full text-xs" data-testid={`import-${model}-rows`}>
                <thead className="bg-slate-50 text-slate-500">
                  <tr><th className="text-left p-2">Riga</th>
                    <th className="text-left p-2">Voce</th>
                    <th className="text-left p-2">Esito</th>
                    <th className="text-left p-2">Errori</th></tr>
                </thead>
                <tbody>
                  {report.rows.map((r) => (
                    <tr key={r.row} className="border-t border-slate-100"
                      data-testid={`import-${model}-row-${r.row}`}>
                      <td className="p-2">{r.row}</td>
                      <td className="p-2">{r.denominazione || r.email || "—"}</td>
                      <td className="p-2">
                        <Badge variant="outline" className={r.status === "ERROR"
                          ? "bg-red-50 text-red-700 border-red-300"
                          : "bg-green-50 text-green-800 border-green-300"}>
                          {r.status}</Badge>
                      </td>
                      <td className="p-2 text-red-700">{r.errors?.join("; ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function PlatformImport() {
  return (
    <div className="space-y-6" data-testid="platform-import-page">
      <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
        <Upload className="w-7 h-7 text-green-800" aria-hidden="true" /> Import massivo CSV
      </h1>
      <p className="text-sm text-slate-600">
        Le aziende importate nascono in PENDING_SETUP con invito admin opzionale;
        gli utenti ricevono un invito di attivazione. Esegui sempre prima l'anteprima.
      </p>
      <ImportCard model="companies" />
      <ImportCard model="users" />
    </div>
  );
}
